
import BottomSheet from '@/components/BottomSheet.vue'
import ProductDisplayModeToggle from '@/components/ProductDisplayModeToggle.vue'
import ProductListCount from '@/components/ProductListCount.vue'
import ProductTechnicalFilters from '@/components/ProductTechnicalFilters.vue'
import { colorTemperatureToneOf } from '@/utils/colorTemperature'
import {
  TOOLBAR_FIT_TIER_NAMES,
  productListToolbarFitClasses,
  productListToolbarNeeds,
} from '@/utils/productListToolbarFit'
import {
  TECHNICAL_FILTERS,
  clampRange,
  parseRangeQuery,
  technicalFilterScale,
  technicalRangeShortSummary,
} from '@/utils/technicalFilters'

const BAR_CLASS = 'has-product-bar'
const COUNT_TOKEN = '{count}'
const LOADING_NUMBER = '0000'

const FOCUS_HANDOVER_MS = 1500
const TIER_ATTRIBUTE = 'data-plt-tier'
const HEIGHT_TOLERANCE = 0.5

let mountedBars = 0
let focusHandover = null

export default {
  name: 'ProductListToolbar',
  components: {
    BottomSheet,
    ProductDisplayModeToggle,
    ProductListCount,
    ProductTechnicalFilters,
  },
  props: {
    total: {
      type: Number,
      default: 0,
    },
    category: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    technicalRanges: {
      type: [Object, Array],
      default: () => ({}),
    },
    sortable: {
      type: Boolean,
      default: true,
    },
    sort: {
      type: String,
      default: null,
    },
    sortOptions: {
      type: Array,
      default: () => [],
    },
    displayMode: {
      type: String,
      default: 'classic',
    },
    enableDisplayModeToggle: {
      type: Boolean,
      default: false,
    },
    enableVehicleSelector: {
      type: Boolean,
      default: false,
    },
    filtersCount: {
      type: Number,
      default: 0,
    },
    hasFilters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortOpen: false,
      sortReturnFocus: null,
      fadeStart: false,
      fadeEnd: false,
    }
  },
  head() {
    return {
      link: [
        {
          hid: 'barlow-preload',
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: '/fonts/barlow-condensed-600.woff2',
          crossorigin: 'anonymous',
        },
      ],
    }
  },
  computed: {
    showCancelSearch() {
      return !!(this.$route.query.q || this.$route.query.technologie)
    },
    hasTools() {
      return this.sortable || this.enableDisplayModeToggle
    },
    fitClasses() {
      if (this.$device.isMobile) return []

      return productListToolbarFitClasses(
        productListToolbarNeeds(this.fitModel)
      )
    },
    fitModel() {
      const locale = this.$i18n.locale
      const results = this.$tc(
        'components.productListToolbar.resultCount',
        this.loading ? 2 : this.total || 0,
        { count: COUNT_TOKEN }
      )
      const hasNumber = results.includes(COUNT_TOKEN)
      const search = this.$route.query.q || this.$route.params.search
      const contextPath = search
        ? 'components.productListToolbar.forQuery'
        : this.category
        ? 'components.productListToolbar.inCategory'
        : null

      return {
        locale,
        count: {
          number: !hasNumber
            ? ''
            : this.loading
            ? LOADING_NUMBER
            : Number(this.total || 0).toLocaleString(locale),
          text: results.replace(COUNT_TOKEN, ''),
          context: contextPath ? this.$t(contextPath, { name: '' }) : '',
          name: contextPath ? search || this.category.name || '' : '',
          cancelable: this.showCancelSearch,
        },
        allFilters: {
          full: this.$t('components.productListToolbar.allFilters'),
          short: this.$t('components.productListToolbar.filters'),
          badge: this.filtersCount ? String(this.filtersCount) : '',
        },
        chips: this.fitChips,
        clearAll: this.hasFilters
          ? this.$t('components.productListToolbar.clearAll')
          : null,
        sort: this.sortable
          ? {
              prefix: this.$t('components.productListToolbar.sortBy', {
                sort: '',
              }),
              value: this.sortLabel,
            }
          : null,
        toggle: this.enableDisplayModeToggle,
      }
    },
    fitChips() {
      return TECHNICAL_FILTERS.map(filter => {
        const { bounds } = technicalFilterScale(this.technicalRanges, filter)
        const selected = parseRangeQuery(this.$route.query[filter.query])
        if (!bounds && !selected) return null

        const name = this.$t(
          `components.productTechnicalFilters.${filter.i18n}`
        )
        if (!selected) return { name }

        const tone =
          bounds && filter.key === 'color_temperature'
            ? colorTemperatureToneOf(clampRange(selected, bounds), bounds)
            : null

        return {
          name,
          orphan: !bounds,
          value: tone
            ? this.$t(`components.colorTemperature.tones.${tone}`)
            : technicalRangeShortSummary(selected, filter, this.$i18n.locale),
        }
      }).filter(Boolean)
    },
    sortLabel() {
      const option = this.sortOptions.find(item => item.value === this.sort)

      return option
        ? option.text
        : this.$t('components.productListToolbar.relevance')
    },
    sortText() {
      return this.$t('components.productListToolbar.sortBy', {
        sort: this.sortLabel,
      })
    },
    allFiltersLabel() {
      const label = this.$t('components.productListToolbar.allFilters')

      return this.filtersCount ? `${label}, ${this.activeFiltersText}` : label
    },
    activeFiltersText() {
      return this.$tc(
        'components.productListToolbar.activeFilterCount',
        this.filtersCount,
        { count: this.filtersCount }
      )
    },
    hasChipRow() {
      return (
        this.enableVehicleSelector ||
        this.hasFilters ||
        this.fitChips.length > 0
      )
    },
  },
  watch: {
    '$route.query'() {
      this.resetScroller()
    },
    technicalRanges() {
      this.$nextTick(this.updateFade)
    },
    fitModel() {
      this.$nextTick(this.scheduleFit)
    },
  },
  created() {
    this.fitFrame = 0
    this.handedOverAt = 0
  },
  mounted() {
    this.takeOverFocus()

    if (!this.$device.isMobile) {
      this.fitTiers()
      window.addEventListener('resize', this.scheduleFit, { passive: true })
      if (document.fonts) document.fonts.ready.then(this.scheduleFit)
      return
    }

    this.updateFade()
    window.addEventListener('resize', this.updateFade, { passive: true })
    mountedBars += 1
    document.body.classList.add(BAR_CLASS)
  },
  beforeDestroy() {
    this.releaseFocus()

    if (!this.$device.isMobile) {
      window.removeEventListener('resize', this.scheduleFit)
      if (this.fitFrame) cancelAnimationFrame(this.fitFrame)
      return
    }

    window.removeEventListener('resize', this.updateFade)
    mountedBars = Math.max(0, mountedBars - 1)
    if (!mountedBars) document.body.classList.remove(BAR_CLASS)
  },
  methods: {
    updateFade() {
      const scroller = this.$refs.scroller
      if (!scroller) return

      const { scrollLeft, scrollWidth, clientWidth } = scroller
      this.fadeStart = scrollLeft > 4
      this.fadeEnd = scrollLeft + clientWidth < scrollWidth - 4
    },
    resetScroller() {
      this.$nextTick(() => {
        if (this.$refs.scroller) this.$refs.scroller.scrollLeft = 0
        this.updateFade()
      })
    },
    clearFilters() {
      this.handOverFocus()
      this.$emit('clear-filters')
    },
    cancelSearch() {
      this.handOverFocus()
      this.$emit('cancel-search')
    },
    handOverFocus() {
      this.handedOverAt = Date.now()
      this.focusAnchor()
    },
    releaseFocus() {
      focusHandover =
        this.handedOverAt && Date.now() - this.handedOverAt < FOCUS_HANDOVER_MS
          ? { at: Date.now(), path: this.$route.path }
          : null
    },
    takeOverFocus() {
      const handover = focusHandover
      focusHandover = null

      if (
        handover &&
        handover.path === this.$route.path &&
        Date.now() - handover.at < FOCUS_HANDOVER_MS
      ) {
        this.focusAnchor()
      }
    },
    focusAnchor() {
      const anchor =
        this.$el &&
        this.$el.querySelector &&
        this.$el.querySelector('.plt__all, .plt-bar__filter')

      if (anchor) anchor.focus()
    },
    scheduleFit() {
      if (this.fitFrame || this.$device.isMobile) return

      this.fitFrame = requestAnimationFrame(() => {
        this.fitFrame = 0
        this.fitTiers()
      })
    },
    fitTiers() {
      const bar = this.$el
      if (!bar || !bar.classList || !bar.classList.contains('plt')) return
      if (!bar.getBoundingClientRect().width) return

      const tiers = TOOLBAR_FIT_TIER_NAMES
      const tightest = tiers[tiers.length - 1]

      bar.setAttribute(TIER_ATTRIBUTE, tightest)
      const shortest = bar.getBoundingClientRect().height
      const roomy = tiers.find(tier => {
        bar.setAttribute(TIER_ATTRIBUTE, tier)

        return bar.getBoundingClientRect().height <= shortest + HEIGHT_TOLERANCE
      })

      bar.setAttribute(TIER_ATTRIBUTE, roomy || tightest)
    },
    openSort(event) {
      this.sortReturnFocus = event.currentTarget
      this.sortOpen = true
    },
    chooseSort(value) {
      this.sortOpen = false
      if (value !== this.sort) this.$emit('update:sort', value)
    },
  },
}
